import { t } from 'i18next';
import * as yup from 'yup';

import { getEmptyErrorMessage, numberSchemaWithTransform } from 'core/yup';
import { PricingProfilePartNumberType } from 'pricing/enums';

import { toNumericValue } from './helpers';

interface IOptions {
  // The cost section is hidden for alternate-version users, whose quotes never use pricing
  // profile costs: their stored costs round-trip untouched and must not be validated.
  withCosts?: boolean;
}

// Tolerant of both phases: yup resolves `when`/`test` siblings against the raw input while
// casting and against the cast object while validating.
const isCostSet = (value: unknown) => toNumericValue(value as string | number) !== null;

export const upsertPricingProfilePartNumberSchema = ({ withCosts = true }: IOptions = {}) => {
  const costSchema = (label: string, testName: string, message: string, sibling: string) => {
    const schema = numberSchemaWithTransform().label(label).nullable();

    if (!withCosts) {
      return schema;
    }

    return schema
      .min(0)
      .test(
        testName,
        message,
        (value, context) => !isCostSet(context.parent?.[sibling]) || isCostSet(value)
      );
  };

  // A cost-only row is valid, so the fixed price/margin is required only when no cost is set.
  const isFixedValueRequired =
    (expectedType: PricingProfilePartNumberType) =>
    (type: string, purchasedCost: unknown, cost: unknown) =>
      expectedType === type && (!withCosts || (!isCostSet(purchasedCost) && !isCostSet(cost)));

  return yup.object().shape({
    part: yup
      .object()
      .label(t('labelPartNumber'))
      .nullable()
      .required(getEmptyErrorMessage(t('labelPartNumber'))),
    uom: yup
      .string()
      .label(t('labelUom'))
      .nullable()
      .required(getEmptyErrorMessage(t('labelUom'))),
    type: yup
      .string()
      .label(t('labelType'))
      .nullable()
      .required(getEmptyErrorMessage(t('labelType'))),
    purchasedCost: costSchema(
      t('labelPurchaseCost'),
      'purchaseCostRequiredWithStandardCost',
      t('errorPricingProfilePartNumberEmptyPurchaseCost'),
      'cost'
    ),
    cost: costSchema(
      t('labelStandardCost'),
      'standardCostRequiredWithPurchaseCost',
      t('errorPricingProfilePartNumberEmptyCost'),
      'purchasedCost'
    ),
    price: numberSchemaWithTransform()
      .label(t('labelFixedPrice'))
      .nullable()
      .when(['type', 'purchasedCost', 'cost'], {
        is: isFixedValueRequired(PricingProfilePartNumberType.PRICE),
        then: (schema) => schema.required(getEmptyErrorMessage(t('labelFixedPrice'))),
      }),
    margin: numberSchemaWithTransform()
      .label(t('labelSpecificMargin'))
      .nullable()
      .when(['type', 'purchasedCost', 'cost'], {
        is: isFixedValueRequired(PricingProfilePartNumberType.MARGIN),
        then: (schema) => schema.required(getEmptyErrorMessage(t('labelSpecificMargin'))),
      }),
  });
};
